import { Dispatch, FC, ReactNode, SetStateAction, useEffect, useMemo, useState } from "react";
import { Flex, Input, Popover, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";

import { Product } from "@/types/product";
import SText from "@/styles/SText";
import { RequirementColumn } from "@/pages/Requirement/type";
import { CellDisabled, MaterialSearchWrap, SelectWrap } from "@/pages/Requirement/style";
import type { ColumnType } from "antd/es/table";
import CommonTable from "@/components/CommonTable";
import ReplaceBomSelector from "./ReplaceBomSelector";
import { getRequirementRowKey, numFormmater } from "@/pages/Requirement/util";
import ListFilterBar from "@/components/ListFilterBar";
import { filterProductItemsByKeyword } from "@/utils/product";


interface MatchBomProps {
  scrollHeight: number;
  bomSubs: Array<RequirementColumn>;
  setBomSubs: Dispatch<SetStateAction<RequirementColumn[]>>;
  toolbarExtra?: ReactNode;
}

const MatchBom: FC<MatchBomProps> = ({
  scrollHeight,
  bomSubs,
  setBomSubs,
  toolbarExtra,
}) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const filteredItems = useMemo(() => filterProductItemsByKeyword(bomSubs, searchKeyword), [bomSubs, searchKeyword]);
  const columns: ColumnType<RequirementColumn>[] = [
    {
      key: "productCode",
      title: <SText text={t("product.code")} />,
      fixed: "left",
      render(value, row, rowIndex) {
        return <>
          <SText text={row?.product?.code || ''} />
          {!!row?.replaceParentId && <SText text={t('bom.itemReq')} style={{ color: "orange" }} />}
        </>
      },
    },
    {
      key: "productName",
      title: <SText text={t("product.name")} />,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.name || ''} />
      },
    },
    {
      key: "model",
      title: <SText text={t("product.brand1")} />,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.model || ''} />
      },
    },
    {
      key: "uom",
      title: <SText text={t("product.uom")} />,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.uom || ''} />
      },
    },
    {
      key: "preReady",
      title: <SText text={t("product.preReady")} />,
      width: 120,
      render(value, row, rowIndex) {
        return (row?.product?.preReady || 0) === 1 ? <Tag color='#87d068' >{t('product.isTrue')}</Tag> : <Tag color='#f50'>{t('product.isFalse')}</Tag>
      },
    },
    {
      key: "useQuantity",
      title: <SText text={t("requirement.itemCountInfo")} />,
      dataIndex: "useQuantity",
      render(value, row, rowIndex) {
        return <SText text={!!row.useQuantity ? numFormmater(Number(row.useQuantity)) : ''} />
      },
    },
    {
      key: "baseQuantity",
      title: <SText text={t("bom.itemBase")} />,
      dataIndex: "baseQuantity",
      render(value, row, rowIndex) {
        return <SText text={!!row.baseQuantity ? numFormmater(Number(row.baseQuantity)) : ''} />
      },
    },
    {
      key: "attritionRate",
      title: <SText text={t("bom.itemAttritionRate")} />,
      dataIndex: "attritionRate",
      render(value, row, rowIndex) {
        return `${row?.attritionRate ?? ''}%`
      },
    },
    {
      key: "useAmount",
      title: <SText text={t("requirement.requireCount")} />,
      dataIndex: "useAmount",
      render(value, row, rowIndex) {
        return <SText text={!!row.useAmount ? numFormmater(Number(row.useAmount)) : ''} />
      },
    },
    {
      key: "replacement",
      width: 260,
      title: <SText text={t("requirement.selectRepl")} />,
      render: (_, record) => <ReplaceBomSelector row={bomSubs.indexOf(record)} bomSubs={bomSubs} setBomSubs={setBomSubs} optMethord="REPLACED" />
    },
    {
      key: "mixedReplacement",
      width: 260,
      title: <SText text={t("requirement.mixReplaced")} />,
      render: (_, record) => <ReplaceBomSelector row={bomSubs.indexOf(record)} bomSubs={bomSubs} setBomSubs={setBomSubs} optMethord="MIXED" />
    },
  ];

  return (
    <Flex vertical gap={8}>
      <Flex gap={8} align="center">
        {toolbarExtra}
        <MaterialSearchWrap>
          <ListFilterBar onSearch={() => setSearchKeyword(keyword)} onReset={() => { setKeyword(""); setSearchKeyword(""); }} showReset={false}>
            {({ submit }) => (
              <Input allowClear value={keyword} onChange={event => setKeyword(event.target.value)} onPressEnter={submit}
                placeholder={`${t("product.code")} / ${t("product.name")} / ${t("product.model")}`}
                aria-label={t("requirement.searchPro")} />
            )}
          </ListFilterBar>
        </MaterialSearchWrap>
      </Flex>
      <CommonTable
        rowKey={getRequirementRowKey}
        columns={columns}
        dataSource={filteredItems}
        scrollHeight={scrollHeight - 40}
      />
    </Flex>
  );
};

export default MatchBom;
